import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { getStripePromise, connectedAccountIdForBusiness } from 'utils/stripePromise';
import { businessData as businessDataAtom } from 'atoms';
import BrandLogo from 'common/BrandLogo';
import Button from 'common/Button';
import ButtonLarge from 'common/ButtonLarge';
import GreenCheck from 'common/GreenCheck';
import HeadingLarge from 'common/HeadingLarge';
import SuccessMessage from 'common/SuccessMessage';
import { useAuth } from 'context/AuthContext';
import AccountCreated from 'emails/AccountCreated';
import PaymentComplete from 'emails/PaymentComplete';
import Layout from 'pages/Payment/Pay/Partials/Layout';
import ReactDOMServer from 'react-dom/server';
import { useRecoilState } from 'recoil';
import { sendEmail } from 'utils/methods';
import { DOC_URLS, openPdfInNewTab } from 'utils/openPdfInNewTab';
import { getBusinessDataApi } from 'SupabseHandler';
import { useEffect, useMemo, useState,useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Constants
const TERMS_VERSION = 'v1.2';
const API_BASE_URL = process.env.REACT_APP_SERVER_URL;
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const PATIENT_PORTAL_URL = process.env.REACT_APP_PATIENT_PORTAL_URL;

// Helper function to parse API response
const parseApiResponse = (response) => {
  return response?.data || response;
};

// Helper to extract UUID from path
const getUuidFromPath = () => window.location.pathname.split('/')[2];


export default function Pay() {
    const { token, loading: authLoading, user } = useAuth();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const businessId = queryParams.get('business_id');
    const acceptTermsOnly = queryParams.get('accept_terms') === 'true';
    
    const [invoice, setInvoice] = useState({});
    const [sessionId, setSessionId] = useState('');
    const [email, setEmail] = useState('');
    const [options, setOptions] = useState({});
    const [business, setBusiness] = useRecoilState(businessDataAtom);
    const [conditions, setConditions] = useState({
        condition1: false,
        condition2: false,
        condition3: false,
        condition4: false,
    });
    const [showStripeCheckout, setShowStripeCheckout] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [emailError, setEmailError] = useState('');

    // For Standard providers the checkout session is created as a direct charge on the
    // connected account, so Embedded Checkout must load Stripe.js in that account's context.
    const connectedAccountId = connectedAccountIdForBusiness(business);
    const stripePromise = useMemo(
        () => getStripePromise(connectedAccountId),
        [connectedAccountId],
    );

    const handleCheckboxChange = (event) => {
        const { name, checked } = event.target;
        setConditions((prevState) => ({
            ...prevState,
            [name]: checked,
        }));
    };

    const handlePayNowClick = async () => {
        if (!Object.values(conditions).every((condition) => condition) || termsAccepted) return;
        try {
            const targetUuid = invoice?.uuid || getUuidFromPath();

            // Narrow, unauthenticated, uuid-scoped endpoint — only ever records
            // consent, nothing financial. See BeSeenProviderBackEnd's
            // invoice.controller.js#acceptInvoiceTerms.
            const response = await fetch(
                `${API_BASE_URL}/api/providers/invoices/uuid/${encodeURIComponent(targetUuid)}/accept-terms`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        terms_accepted_at: new Date().toISOString(),
                        terms_version: TERMS_VERSION,
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to update invoice');
            }

            if (acceptTermsOnly) {
                setTermsAccepted(true);
            } else {
                setShowStripeCheckout(true);
            }
        } catch (e) {
            console.error('Error updating invoice:', e);
        }
    };

    const fetchClientSecret = useCallback(async () => {
        // The backend re-derives amount/platformFee/line items itself from the invoice
        // (looked up by uuid) and its business — it no longer trusts any of those
        // values from this request. See BeSeenProviderBackEnd's
        // paymentMethods.controller.js#createCheckoutSession.
        return fetch(`${API_BASE_URL}/create-checkout-session`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                uuid: invoice.uuid,
                email: invoice?.info_source_data || email || '',
            }),
        })
            .then((res) => res.json())
            .then((data) => {
                return data?.data?.clientSecret ?? data?.clientSecret;
            })
            .catch((error) => {
                console.error('Error in fetchClientSecret:', error);
                return null;
            });
    }, [invoice, email]);
    const fetchBusinessData = async (id) => {
        try {
            if (!id) {
                console.error('No business ID provided for fetchBusinessData');
                return;
            }
            const businessData = await getBusinessDataApi(id);
            
            if (Array.isArray(businessData) && businessData.length > 0) {
                setBusiness(businessData[0]);
            } else {
                console.error('No business data found for ID:', id);
            }
        } catch (error) {
            console.error('Error fetching business data:', error);
        }
    };
    const fetchInvoiceData = async (uuid, isAuthenticated) => {
        try {
            if (isAuthenticated && !token) {
                console.error('No authentication token found');
                return null;
            }

            const headers = {
                'Content-Type': 'application/json',
                ...(isAuthenticated && { 'Authorization': `Bearer ${token}` }),
            };

            let response;
            if (isAuthenticated) {
                // Fetch with authentication
                response = await fetch(`${API_BASE_URL}/api/providers/get-invoices`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        uuid,
                        isDeleted: false,
                    }),
                });
            } else {
                // Public fetch without authentication
                response = await fetch(
                    `${API_BASE_URL}/api/providers/invoices/uuid/${encodeURIComponent(uuid)}?isDeleted=eq.false`,
                    {
                        method: 'GET',
                        headers,
                    },
                );
            }

            if (!response.ok) {
                console.error('Failed to fetch invoice:', response.status);
                return null;
            }

            const result = await response.json();
            const data = parseApiResponse(result);

            if (Array.isArray(data) && data.length > 0) {
                const invoiceData = data[0];
                setInvoice(invoiceData);
                setEmail(invoiceData?.info_source_data || '');
                if (invoiceData?.payment_id) {
                    setSessionId(invoiceData.payment_id);
                }
                return invoiceData;
            }

            return null;
        } catch (error) {
            console.error('Error fetching invoice:', error);
            return null;
        }
    };

    useEffect(() => {
        const loadData = async () => {
            if (authLoading) return; // Wait for auth to finish loading

            const uuid = getUuidFromPath();
            const returnedSessionId = new URLSearchParams(location.search).get('session_id');

            const invoiceData = await fetchInvoiceData(uuid, !!token);

            if (invoiceData) {
                // Fetch business data using either query param or invoice's business_id
                const businessIdToUse = businessId || invoiceData?.business_id;
                if (businessIdToUse) {
                    await fetchBusinessData(businessIdToUse);
                }

                // Handle payment success callback
                if (returnedSessionId) {
                    setSessionId(returnedSessionId);
                    handlePaymentSuccess(uuid, returnedSessionId);
                }
            }
        };

        loadData();
    }, [authLoading, token, user, location.search, businessId]);

    useEffect(() => {
        if (!invoice?.total) return;

        setOptions({
            fetchClientSecret,
        });
    }, [invoice?.total, fetchClientSecret]);

    const handlePaymentSuccess = async (uuid, sessionId) => {
       

        try {
            // Narrow, unauthenticated, uuid-scoped endpoint — the backend independently
            // verifies with Stripe that this session actually succeeded and belongs to
            // this invoice before flipping status to 'paid'; it doesn't trust this call
            // just because we're making it. See BeSeenProviderBackEnd's
            // invoice.controller.js#confirmInvoicePayment.
            const updateResponse = await fetch(
                `${API_BASE_URL}/api/providers/invoices/uuid/${encodeURIComponent(uuid)}/confirm-payment`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        payment_id: sessionId,
                    }),
                }
            );

            if (!updateResponse.ok) {
                const errorData = await updateResponse.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to update invoice');
            }

            const updateResult = await updateResponse.json();
            const data = parseApiResponse(updateResult);
            const invoiceData = Array.isArray(data) ? data[0] : data;

            const invoiceDetails = {
                patientEmail: invoiceData?.info_source_data,
                invoiceTotal: invoiceData?.total,
                providerId: invoiceData?.provider_id,
                businessId: invoiceData?.business_id,
            };

            let details = null;
            if (invoiceDetails?.providerId) {
                // Fetch provider data from backend API
                const providerResponse = await fetch(
                    `${API_BASE_URL}/api/providers/provider/${invoiceDetails.providerId}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                    },
                );

                if (providerResponse.ok) {
                    const providerResult = await providerResponse.json();
                    const providerData = providerResult.success ? providerResult.data || [] : providerResult;
                    details = Array.isArray(providerData) ? providerData : [providerData];
                } else {
                    console.error('Failed to fetch provider data');
                    details = [];
                }
            } else if (invoiceDetails?.businessId) {
                // Fetch business data from backend API
                const businessResponse = await fetch(
                    `${API_BASE_URL}/api/providers/get-business`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                        body: JSON.stringify({ userid: user?.id })
                    }
                );

                if (businessResponse.ok) {
                    const businessResult = await businessResponse.json();
                    details = parseApiResponse(businessResult);
                } else {
                    console.error('Failed to fetch business data');
                }
            }
        
            const receiptQuery = connectedAccountId ? `?stripeAccountId=${connectedAccountId}` : '';
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/v1/stripe/checkout-receipt/${sessionId}${receiptQuery}`,
                {
                  method: "GET",
                  headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                  },
                }
              );
            
              if (!response.ok) {
                throw new Error("Failed to fetch receipt");
              }
            
              const { receiptUrl } = await response.json();

            const doctorName = invoiceDetails?.providerId
                ? `${details?.[0]?.first_name} ${details?.[0]?.last_name}`
                : details?.name;
            const emailContent = `
                <!DOCTYPE html>
                ${ReactDOMServer.renderToString(<PaymentComplete drName={doctorName} amount={invoiceDetails?.invoiceTotal} invoiceUrl={receiptUrl} services={invoiceData?.service_data} />)}
            `;

            // Create provider-specific email content with patient name
            const providerEmailContent = `
                <!DOCTYPE html>
                ${ReactDOMServer.renderToString(<PaymentComplete drName={invoiceData?.patient_name} amount={invoiceDetails?.invoiceTotal} invoiceUrl={receiptUrl} isProviderNotification={true} services={invoiceData?.service_data} />)}
            `;

            setEmail(invoiceDetails?.patientEmail);

            sendEmail(
                token,
                invoiceDetails?.patientEmail,
                'Payment Successful',
                ' ',
                emailContent,
                'Payment confirmation mail has been sent to email id',
            );

            const providerBusinessEmail = invoiceDetails?.providerId ? details?.[0]?.email : details?.email;

            if (providerBusinessEmail) {
                sendEmail(
                    token,
                    providerBusinessEmail,
                    'Payment Received',
                    ' ',
                    providerEmailContent,
                    'Payment notification sent to provider/business',
                );
            } else {
            }
        } catch (error) {
            console.error('Error in handlePaymentSuccess:', error);
        }
    };
    const handleCreateAccount = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const err = emailRegex.test(email) ? '' : 'Invalid email address';
        setEmailError(err);
        
        if (err) {
            return;
        }

        if (email) {
            sendEmail(
                token,
                email,
                'Welcome to Be:Seen Health',
                ' ',
                `
                <!DOCTYPE html>
                ${ReactDOMServer.renderToString(<AccountCreated name={invoice.patient_name} email={email} />)}`,
                'Account Created Successfully!',
            );
        }

        setTimeout(() => {
            window.location.href = `${PATIENT_PORTAL_URL}/auth/signup?email=${encodeURIComponent(email)}`;
        }, 2000);
    };
    return (
        <>
            <style>{`
                .orange-text {
                    color: #f97316;
                    text-decoration: underline;
                    cursor: pointer;
                }
                .orange-text:hover {
                    color: #ea580c;
                }
            `}</style>
            <BrandLogo></BrandLogo>
            <Layout className="">
                <div className="flex flex-col md:flex-row">
                    <div className="light-background min-h-screen w-full md:w-1/2 px-4 md:px-8 lg:px-16 xl:px-36 pt-8 md:pt-20">
                        {sessionId ? (
                            <>
                                <SuccessMessage>Payment Complete!</SuccessMessage>

                                <HeadingLarge>Thanks, {invoice?.patient_name}</HeadingLarge>

                                <h2 className="mb-5 text-lg font-semibold text-gray-400">
                                    We've sent a receipt to your email.
                                </h2>
                            </>
                        ) : (
                            <>
                                <h2 className="mb-2 text-lg font-semibold text-gray-400">Office Visit Payment</h2>
                                <div className="mb-8 text-5xl font-bold text-gray-900">${invoice?.total}</div>
                            </>
                        )}

                        <div className="mb-4">
                            <h3
                                className="mb-2 text-lg font-semibold"
                                style={{
                                    borderBottom: '1px solid #C4C4C4',
                                    paddingBottom: '10px',
                                }}
                            >
                                Payment Details
                            </h3>
                            <div className="mb-1 mt-6 flex justify-between">
                                <span className="text-black">Cost of Visit</span>
                            </div>
                            <div
                                className="mb-8 flex justify-between"
                                style={{
                                    borderBottom: '1px solid #C4C4C4',
                                    paddingBottom: '24px',
                                }}
                            >
                                <div className="font-light text-gray-700">
                                    {Array.isArray(invoice?.service_data) && invoice?.service_data?.length > 0 ? (
                                        invoice.service_data.map((item, idx) => {
                                            const name =
                                                typeof item === 'string'
                                                    ? item
                                                    : item?.name ||
                                                      item?.title ||
                                                      item?.service_name ||
                                                      item?.serviceName ||
                                                      item?.procedure_name ||
                                                      'Service';
                                            return <div key={idx}>{name}</div>;
                                        })
                                    ) : (
                                        <span>Service</span>
                                    )}
                                </div>
                                <span className="text-gray-900">${invoice?.total}</span>
                            </div>
                        </div>
                        <div className="mt-12 flex justify-between text-lg font-bold">
                            <span>Total Due</span>
                            <span>${invoice?.total}</span>
                        </div>

                        {/* Instructions moved to left column */}
                        {!sessionId && !showStripeCheckout && (
                            <div className="mt-8 mb-8">
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                                    Review & confirm your payment
                                </h3>
                                <p className="text-gray-700 mb-6 leading-relaxed">
                                    Check the boxes to acknowledge the terms, then select Confirm & Pay.
                                </p>

                                <div className="space-y-3 mb-6 text-left">
                                    <div className="flex items-start gap-3">
                                        <span className="text-gray-500 text-lg font-semibold">1.</span>
                                        <span className="text-gray-700">Review services and pricing</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-gray-500 text-lg font-semibold">2.</span>
                                        <span className="text-gray-700">Required legal checkboxes</span>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-gray-500 text-lg font-semibold">3.</span>
                                        <span className="text-gray-700">Confirm & Pay CTA</span>
                                    </div>
                                </div>

                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2">
                                        <span className="text-blue-500 text-lg font-bold">!</span>
                                        <p className="text-blue-800 text-sm">
                                            If any boxes remain unchecked, you will not be able to proceed with the
                                            payment.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Legal checkboxes - hide after payment */}
                        {!sessionId && (
                            <div className="mt-8">
                                <div className={'mb-4 text-xl font-semibold'}>By proceeding to pay, I hereby:</div>

                                <div className="mb-6 flex flex-col gap-4 text-gray-700 text-sm">
                                    <div className="flex gap-3 items-start">
                                        <input
                                            type="checkbox"
                                            className="cursor-pointer mt-1"
                                            id="condition1"
                                            name="condition1"
                                            checked={conditions.condition1}
                                            onChange={handleCheckboxChange}
                                        />
                                        <label htmlFor="condition1" className="cursor-pointer">
                                            You have read and consent to the{' '}
                                            <strong
                                                className="text-orange-500 underline cursor-pointer"
                                                onClick={() => openPdfInNewTab(DOC_URLS.termsAndConditionDoc)}
                                            >
                                                Terms and Conditions
                                            </strong>{' '}
                                            and the{' '}
                                            <strong
                                                className="text-orange-500 underline cursor-pointer"
                                                onClick={() => openPdfInNewTab(DOC_URLS.privacyPolicyDoc)}
                                            >
                                                Privacy Policy
                                            </strong>{' '}
                                            of Be Seen Health, LLC.
                                        </label>
                                    </div>

                                    <div className="flex gap-3 items-start">
                                        <input
                                            type="checkbox"
                                            className="cursor-pointer mt-1"
                                            id="condition2"
                                            name="condition2"
                                            checked={conditions.condition2}
                                            onChange={handleCheckboxChange}
                                        />
                                        <label htmlFor="condition2" className="cursor-pointer">
                                            You acknowledge that Providers do not accept Medicare, Medicaid, or any
                                            other third-party insurance for care rendered through Be:Seen Health. By
                                            electing to use the services and schedule care through Be:Seen, you are
                                            solely responsible for paying out of pocket in accordance with the usual and
                                            customary fees established by providers.
                                        </label>
                                    </div>

                                    <div className="flex gap-3 items-start">
                                        <input
                                            type="checkbox"
                                            className="cursor-pointer mt-1"
                                            id="condition3"
                                            name="condition3"
                                            checked={conditions.condition3}
                                            onChange={handleCheckboxChange}
                                        />
                                        <label htmlFor="condition3" className="cursor-pointer">
                                            You understand that services do not have a scheduled time slot.
                                        </label>
                                    </div>

                                    <div className="flex gap-3 items-start">
                                        <input
                                            type="checkbox"
                                            className="cursor-pointer mt-1"
                                            id="condition4"
                                            name="condition4"
                                            checked={conditions.condition4}
                                            onChange={handleCheckboxChange}
                                        />
                                        <label htmlFor="condition4" className="cursor-pointer">
                                            You acknowledge and have read the{' '}
                                            <strong
                                                className="text-orange-500 underline cursor-pointer"
                                                onClick={() => openPdfInNewTab(DOC_URLS.disclaimerWithMedicareUpdate)}
                                            >
                                                Booking Disclaimer with Medicare Update
                                            </strong>
                                            .
                                        </label>
                                    </div>
                                </div>

                                {!showStripeCheckout && (
                                    <Button
                                        onClick={handlePayNowClick}
                                        disabled={
                                            !Object.values(conditions).every((condition) => condition) || termsAccepted
                                        }
                                        className="w-full mt-4"
                                    >
                                        Pay Now
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="light-background-2 min-h-screen w-full md:w-1/2 border-t md:border-t-0 md:border-l px-4 md:px-8 lg:px-16 xl:px-36 pt-8">
                        <div>
                            {invoice?.total && !sessionId ? (
                                <>
                                    {acceptTermsOnly && termsAccepted ? (
                                        <div className="pt-32 flex justify-center">
                                            <div className="text-center max-w-md">
                                                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                                                    Thank you for accepting the terms
                                                </h2>
                                                <p className="text-gray-700 mb-6 leading-relaxed">
                                                    Your acknowledgment has been recorded successfully.
                                                </p>
                                            </div>
                                        </div>
                                    ) : showStripeCheckout ? (
                                        <div className="stripe-checkout-container w-full">
                                            <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
                                                <EmbeddedCheckout />
                                            </EmbeddedCheckoutProvider>
                                        </div>
                                    ) : (
                                        <div className="pt-32 flex justify-center">
                                            <div className="text-center max-w-md">
                                                <h2 className="text-2xl font-bold text-gray-900 mb-6">Need Help?</h2>
                                                <p className="text-gray-700 mb-6 leading-relaxed">
                                                    We're here to assist you with your payment and booking process.
                                                </p>

                                                <div className="space-y-4 mb-6 text-left">
                                                    <div className="bg-gray-50 rounded-lg p-4">
                                                        <h3 className="font-semibold text-gray-900 mb-2">
                                                            💳 Payment Security
                                                        </h3>
                                                        <p className="text-sm text-gray-700">
                                                            Your payment information is encrypted and processed securely
                                                            through Stripe.
                                                        </p>
                                                    </div>

                                                    <div className="bg-gray-50 rounded-lg p-4">
                                                        <h3 className="font-semibold text-gray-900 mb-2">
                                                            📧 Receipt & Confirmation
                                                        </h3>
                                                        <p className="text-sm text-gray-700">
                                                            You'll receive an email confirmation and receipt once
                                                            payment is processed.
                                                        </p>
                                                    </div>

                                                    <div className="bg-gray-50 rounded-lg p-4">
                                                        <h3 className="font-semibold text-gray-900 mb-2">
                                                            ❓ Have Questions?
                                                        </h3>
                                                        <p className="text-sm text-gray-700">
                                                            Contact our support team if you need assistance with your
                                                            booking or payment.
                                                        </p>
                                                    </div>
                                                </div>

                                                <p className="text-gray-600 text-sm">
                                                    Be:Seen Health - Connecting you with quality healthcare providers
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className={'pt-20'}>
                                    <div className="heading">
                                        Want to easily book healthcare appointments and make out of pocket payments ?{' '}
                                    </div>
                                    <div className="subHeading">Create a Be:Seen Health account:</div>
                                    <div className="parentList">
                                        <div className="list">
                                            <GreenCheck></GreenCheck>
                                            <div className="text">Find and access specialists without a referral</div>
                                        </div>

                                        <div className="list">
                                            <GreenCheck></GreenCheck>
                                            <div className="text">Conveniently compare, book, and pay online</div>
                                        </div>

                                        <div className="list">
                                            <GreenCheck></GreenCheck>
                                            <div className="text">
                                                Transparent pricing without surprise medical bills
                                            </div>
                                        </div>
                                    </div>

                                    <div className="field">
                                        <fieldset className="fieldSet">
                                            <label htmlFor="email" className="block text-sm font-medium">
                                                Email
                                            </label>
                                            <input
                                                className={`common-input p-2.5 px-5 focus:!bg-white ${emailError?.length > 0 ? 'error-field' : ''}`}
                                                autoFocus={true}
                                                type="email"
                                                placeholder="Enter your email"
                                                value={email}
                                                name="email"
                                                required={true}
                                                onChange={(e) => {
                                                    setEmail(e.target.value);
                                                }}
                                            />
                                            {emailError && <p style={{ color: 'red', textAlign: 'center' }}>{emailError}</p>}
                                        </fieldset>
                                    </div>

                                    <div className="legalText">
                                        By creating your Be:Seen account, you agree to the{' '}
                                        <a
                                            className="orange-text"
                                            onClick={() => openPdfInNewTab(DOC_URLS.termsAndConditionDoc)}
                                        >
                                            Terms and Conditions
                                        </a>{' '}
                                        and the{' '}
                                        <a
                                            className="orange-text"
                                            onClick={() => openPdfInNewTab(DOC_URLS.privacyPolicyDoc)}
                                        >
                                            Privacy Policy
                                        </a>{' '}
                                        of Be Seen Health, LLC
                                    </div>

                                    <ButtonLarge
                                        onClick={handleCreateAccount}
                                        disabled={!email}
                                        className="w-full font-bold mt-5"
                                        loading={false}
                                    >
                                        Create your Be:Seen account
                                    </ButtonLarge>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    );
}
